import { FC, useState } from "react";
import styles from "./Sidebar.module.scss";
import { clientContent } from "../../client/content";
import { PulseLogo } from "../PulseLogo";

export interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const informationCategories = [
  {
    name: "Asset Data",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Certification",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Controls",
    children: ["Electrical", "Mechanical"],
  },
  {
    name: "Descriptions",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Design Criteria",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Environmental",
    children: [],
  },
  {
    name: "General Information",
    children: [],
  },
  {
    name: "Health & Safety",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Maintenance",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Manufacturer Information",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Operating Procedures",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Schedules",
    children: ["Building", "Electrical", "Mechanical"],
  },
];

export const Sidebar: FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";
  const isDesignCriteriaActive = currentPath.startsWith("/design-criteria");
  const isDrawingsActive = currentPath.startsWith("/drawings");
  const isAssetDataActive =
    currentPath.startsWith("/asset-data") ||
    currentPath.startsWith("/mobile-asset");
  const isSearchActive = currentPath.startsWith("/search");
  const isOverviewActive = currentPath === "/" || currentPath === "";
  const isHealthSafetyActive =
    currentPath.startsWith("/health-and-safety") ||
    currentPath.startsWith("/health-safety") ||
    currentPath.startsWith("/hs");

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    () => ({
      "Health & Safety": isHealthSafetyActive,
      "Design Criteria": isDesignCriteriaActive,
    })
  );

  const toggleCategory = (
    name: string,
    hasChildren: boolean,
    e: React.MouseEvent,
  ) => {
    if (hasChildren) {
      e.preventDefault();
      setOpenCategories((prev) => ({
        ...prev,
        [name]: !prev[name],
      }));
    }
  };

  return (
    <>
      <div
        className={`${styles.sidebarBackdrop} ${isOpen ? styles.open : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}
        aria-label="Main navigation"
      >
        <div className={styles.topSection}>
          <div className={styles.brandHeader}>
            <a
              href="/"
              className={styles.brand}
              aria-label="Pulse Building Information Home"
            >
              <PulseLogo variant="full" theme="dark" />
            </a>
            {onClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close navigation sidebar"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className={styles.siteSwitcher}>
            <span className={styles.sectionLabel} id="current-site-label">
              CURRENT SITE
            </span>
            <button
              type="button"
              className={styles.siteCard}
              aria-labelledby="current-site-label"
            >
              <span className={styles.siteName}>
                {clientContent.clientName}
              </span>
              <span className={styles.siteSubtext}>
                {clientContent.siteLocation}
              </span>
            </button>
          </div>

          <nav className={styles.navSection} aria-label="Explore sections">
            <span className={styles.sectionLabel}>EXPLORE</span>
            <ul className={styles.navList}>
              <li>
                <a
                  href="/"
                  className={`${styles.navLink} ${isOverviewActive ? styles.active : ""}`}
                  aria-current={isOverviewActive ? "page" : undefined}
                >
                  <span>Overview</span>
                </a>
              </li>
              <li>
                <a
                  href="/search"
                  className={`${styles.navLink} ${isSearchActive ? styles.active : ""}`}
                  aria-current={isSearchActive ? "page" : undefined}
                >
                  <span>Search</span>
                </a>
              </li>
              <li>
                <a
                  href="/drawings"
                  className={`${styles.navLink} ${isDrawingsActive ? styles.active : ""}`}
                  aria-current={isDrawingsActive ? "page" : undefined}
                >
                  <span>Drawings</span>
                  <span className={styles.navChevron} aria-hidden="true">
                    ›
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="/design-criteria/mechanical"
                  className={`${styles.navLink} ${isDesignCriteriaActive ? styles.active : ""}`}
                  aria-current={isDesignCriteriaActive ? "page" : undefined}
                >
                  <span>Design Criteria Mechanical</span>
                </a>
              </li>
              <li>
                <a
                  href="/asset-data/ahu-003"
                  className={`${styles.navLink} ${isAssetDataActive ? styles.active : ""}`}
                  aria-current={isAssetDataActive ? "page" : undefined}
                >
                  <span>Asset Concept</span>
                </a>
              </li>
            </ul>
          </nav>

          <nav className={styles.navSection} aria-label="Information sections">
            <span className={styles.sectionLabel}>INFORMATION</span>
            <ul className={styles.navList}>
              {informationCategories.map((cat) => {
                const hasChildren = cat.children.length > 0;
                const isOpen = !!openCategories[cat.name];

                return (
                  <li key={cat.name} className={styles.infoCategoryItem}>
                    <a
                      href={
                        cat.name === "Design Criteria"
                          ? "/design-criteria/mechanical"
                          : cat.name === "Health & Safety"
                          ? "/health-and-safety/electrical"
                          : "/more-info"
                      }
                      className={`${styles.navLink} ${
                        (cat.name === "Design Criteria" && isDesignCriteriaActive) ||
                        (cat.name === "Health & Safety" && isHealthSafetyActive)
                          ? styles.active
                          : ""
                      }`}
                      onClick={(e) => toggleCategory(cat.name, hasChildren, e)}
                    >
                      <span>{cat.name}</span>
                      {hasChildren && (
                        <span
                          className={`${styles.navChevron} ${isOpen ? styles.open : ""}`}
                          aria-hidden="true"
                        >
                          ›
                        </span>
                      )}
                    </a>
                    {hasChildren && isOpen && (
                      <ul className={styles.childNavList}>
                        {cat.children.map((child) => {
                          const isChildActive =
                            (cat.name === "Design Criteria" &&
                              child.toLowerCase() === "mechanical" &&
                              isDesignCriteriaActive) ||
                            (cat.name === "Health & Safety" &&
                              child.toLowerCase() === "electrical" &&
                              isHealthSafetyActive);

                          const childHref =
                            cat.name === "Design Criteria" &&
                            child.toLowerCase() === "mechanical"
                              ? "/design-criteria/mechanical"
                              : cat.name === "Health & Safety" &&
                                child.toLowerCase() === "electrical"
                              ? "/health-and-safety/electrical"
                              : "/more-info";

                          return (
                            <li key={child}>
                              <a
                                href={childHref}
                                className={`${styles.childNavLink} ${isChildActive ? styles.active : ""}`}
                                aria-current={isChildActive ? "page" : undefined}
                              >
                                {child}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className={styles.helpSection}>
          <span className={styles.helpLabel}>Need help?</span>
          <div className={styles.helpLinks}>
            <a href="/more-info">Pulse guide</a>
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            <a href="/more-info">Contact support</a>
          </div>
          <div className={styles.copyright}>
            Copyright tsaservices.co.uk 2026
          </div>
        </div>
      </aside>
    </>
  );
};
